import requestService from './requestService';
import {
  isTrustedS3Url,
  createAttachmentError,
  AttachmentError,
} from './attachmentUtils';

// ============================================================================
// Resumable S3 multipart upload client (large translation source files)
//
// The browser only issues PUTs to presigned UploadPart URLs. CreateMultipartUpload,
// CompleteMultipartUpload, AbortMultipartUpload and ListParts all run server-side.
// Large files are chunked into fixed-size parts, uploaded with bounded concurrency,
// and any interrupted upload can be resumed (even across sessions) via localStorage
// + the server-side ListParts status endpoint.
// ============================================================================

// Fixed size for every part except the last (16 MiB). MUST match the backend.
export const PART_SIZE = 16 * 1024 * 1024;

// Upload tuning. The per-part timeout is intentionally generous (5 min) so a single
// ~16 MiB part finishes well inside it even on a slow/4G link — unlike the global 45s
// single-PUT timeout that this flow replaces.
const UPLOAD_CONCURRENCY = 3;
const PART_MAX_RETRIES = 3;
const PART_TIMEOUT_MS = 5 * 60 * 1000;
const RETRY_BASE_DELAY_MS = 500;

// localStorage resume key: one in-flight multipart upload per prebooking + file (name+size).
const RESUME_KEY_PREFIX = 'tulka.mpu.';

// Resume entries older than this are useless: the bucket's AbortIncompleteMultipartUpload
// lifecycle rule reaps the S3 upload after 7 days. Purged on every multipart upload.
const RESUME_ENTRY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const resumeKey = (prebookingId, fileName, fileSize) => `${RESUME_KEY_PREFIX}${prebookingId}.${fileName}.${fileSize}`;

const multipartPath = (prebookingId) =>
  `prebooking/${encodeURIComponent(prebookingId)}/attachment/multipart`;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// Backend HTTP client (platform apiType, no /api/v1 prefix)
// ---------------------------------------------------------------------------

/**
 * Create the S3 multipart upload + attachment record.
 * @returns {Promise<{attachmentId: string, uploadId: string, partSize: number, partCount: number, expiresIn: number}>}
 */
export const initiateMultipart = async (prebookingId, { filename, fileSize, contentType, unitScope, unitType }) => {
  try {
    const body = { filename, fileSize, contentType };
    if (unitScope !== null && unitScope !== undefined) body.unitScope = unitScope;
    if (unitType !== null && unitType !== undefined) body.unitType = unitType;
    return await requestService.postWithJson(
      `${multipartPath(prebookingId)}/initiate`,
      null,
      body,
      { apiType: 'platform' }
    );
  } catch (e) {
    console.error('Failed to initiate multipart upload:', e);
    throw createAttachmentError(e, 'Failed to initiate upload');
  }
};

/**
 * Presign a batch of UploadPart URLs (1-based partNumbers).
 * @returns {Promise<{urls: Array<{partNumber: number, url: string}>, expiresIn: number}>}
 */
export const getPartUrls = async (prebookingId, attachmentId, partNumbers) => {
  try {
    return await requestService.postWithJson(
      `${multipartPath(prebookingId)}/part-urls`,
      null,
      { attachmentId, partNumbers },
      { apiType: 'platform' }
    );
  } catch (e) {
    console.error('Failed to get multipart part URLs:', e);
    throw createAttachmentError(e, 'Failed to get part URLs');
  }
};

/**
 * Complete the multipart upload with the collected ETags.
 * @param {Array<{partNumber: number, etag: string}>} parts
 * @returns {Promise<{success: boolean, attachment: object}>}
 */
export const completeMultipart = async (prebookingId, attachmentId, parts) => {
  try {
    return await requestService.postWithJson(
      `${multipartPath(prebookingId)}/complete`,
      null,
      { attachmentId, parts },
      { apiType: 'platform' }
    );
  } catch (e) {
    console.error('Failed to complete multipart upload:', e);
    throw createAttachmentError(e, 'Failed to complete upload');
  }
};

/**
 * Abort the multipart upload and soft-delete the record. Idempotent server-side.
 * @returns {Promise<{success: boolean}>}
 */
export const abortMultipart = async (prebookingId, attachmentId) => {
  try {
    return await requestService.postWithJson(
      `${multipartPath(prebookingId)}/abort`,
      null,
      { attachmentId },
      { apiType: 'platform' }
    );
  } catch (e) {
    console.error('Failed to abort multipart upload:', e);
    throw createAttachmentError(e, 'Failed to abort upload');
  }
};

/**
 * Fetch multipart status + already-uploaded parts (for resume).
 * @returns {Promise<{attachmentId: string, uploadId: string, status: string, filename: string,
 *   fileSize: number, partSize: number, partCount: number, uploadedParts: Array<{partNumber: number, size: number, etag: string}>}>}
 */
export const getMultipartStatus = async (prebookingId, attachmentId) => {
  try {
    return await requestService.get(
      `${multipartPath(prebookingId)}/status`,
      `attachmentId=${encodeURIComponent(attachmentId)}`,
      { apiType: 'platform' }
    );
  } catch (e) {
    console.error('Failed to get multipart status:', e);
    throw createAttachmentError(e, 'Failed to get upload status');
  }
};

// ---------------------------------------------------------------------------
// localStorage resume helpers (best-effort; storage may be unavailable)
// ---------------------------------------------------------------------------

const readResumeEntry = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeResumeEntry = (key, entry) => {
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (e) {
    // Private mode / quota exceeded — resume is best-effort, so ignore.
  }
};

const clearResumeEntry = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
};

/**
 * Drop resume entries whose S3 upload has certainly been reaped by the bucket's
 * 7-day AbortIncompleteMultipartUpload lifecycle rule (or that are unreadable).
 */
const purgeExpiredResumeEntries = () => {
  try {
    const now = Date.now();
    const staleKeys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.indexOf(RESUME_KEY_PREFIX) === 0) {
        const entry = readResumeEntry(key);
        if (!entry || !entry.createdAt || now - entry.createdAt > RESUME_ENTRY_TTL_MS) {
          staleKeys.push(key);
        }
      }
    }
    staleKeys.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    // storage unavailable — nothing to purge
  }
};

// ---------------------------------------------------------------------------
// Chunking
// ---------------------------------------------------------------------------

/**
 * Split a file size into 1-based parts of `partSize` (last part is the remainder).
 * @returns {Array<{partNumber: number, start: number, end: number}>}
 */
export const computeParts = (fileSize, partSize = PART_SIZE) => {
  if (!fileSize || fileSize <= 0) return [];
  const partCount = Math.max(1, Math.ceil(fileSize / partSize));
  const parts = [];
  for (let i = 0; i < partCount; i += 1) {
    const start = i * partSize;
    const end = Math.min(start + partSize, fileSize);
    parts.push({ partNumber: i + 1, start, end });
  }
  return parts;
};

// ---------------------------------------------------------------------------
// Single-part PUT via XHR (per-part timeout + progress + ETag capture)
// ---------------------------------------------------------------------------

const uploadPart = ({ url, blob, timeoutMs = PART_TIMEOUT_MS, onProgress, signal }) =>
  new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
      reject(createAttachmentError(new Error('Upload aborted'), 'Upload cancelled'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.responseType = 'text';
    xhr.timeout = timeoutMs;
    // Do NOT set x-amz-server-side-encryption / Content-Type as signed headers on
    // UploadPart — SSE is signed on CreateMultipartUpload only; a signed header here
    // would break the presigned-PUT signature. Content-Length is set by the browser.

    let onAbort = null;
    const cleanup = () => {
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
    };

    if (signal) {
      onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort);
    }

    if (typeof onProgress === 'function') {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded);
      };
    }

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader('ETag'), status: xhr.status });
      } else {
        const error = new Error(`S3 part upload failed with status ${xhr.status}`);
        error.status = xhr.status;
        reject(error);
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(createAttachmentError(new Error('Network error during part upload'), 'Failed to upload file'));
    };
    xhr.ontimeout = () => {
      cleanup();
      const error = new Error('Part upload timed out');
      error.status = 408;
      reject(error);
    };
    xhr.onabort = () => {
      cleanup();
      reject(createAttachmentError(new Error('Upload aborted'), 'Upload cancelled'));
    };

    xhr.send(blob);
  });

/**
 * Provider that hands out a presigned URL per part. Batch-prefetched URLs are consumed
 * once; any later request (e.g. after a 403 expiry) triggers a fresh single presign.
 */
const makeUrlProvider = (prebookingId, attachmentId, prefetchedEntries) => {
  const cache = new Map(prefetchedEntries);
  return async (partNumber) => {
    if (cache.has(partNumber)) {
      const url = cache.get(partNumber);
      cache.delete(partNumber);
      return url;
    }
    const res = await getPartUrls(prebookingId, attachmentId, [partNumber]);
    const found = (res.urls || []).find((u) => u.partNumber === partNumber);
    if (!found) {
      throw createAttachmentError(new Error(`No presigned URL returned for part ${partNumber}`), 'Failed to get part URLs');
    }
    return found.url;
  };
};

const getStatusCode = (error) => error?.status || error?.response?.status || null;

/**
 * Upload one part with retry + backoff. Re-fetches the presigned URL on 403 (expired).
 * Every URL is validated with isTrustedS3Url before the PUT. Returns the part ETag.
 */
const uploadPartWithRetry = async ({ part, blob, getUrlForPart, onPartProgress, signal }) => {
  let lastError = null;
  let url = null;

  for (let attempt = 0; attempt < PART_MAX_RETRIES; attempt += 1) {
    if (signal && signal.aborted) {
      throw createAttachmentError(new Error('Upload aborted'), 'Upload cancelled');
    }
    try {
      if (!url) {
        try {
          url = await getUrlForPart(part.partNumber);
        } catch (urlError) {
          // A backend 4xx on presigning is deterministic (bad state / no access /
          // expired upload) — retrying the part cannot fix it, so fail fast.
          const urlStatus = getStatusCode(urlError);
          if (urlStatus && urlStatus >= 400 && urlStatus < 500 && urlStatus !== 429) {
            urlError.fatal = true;
          }
          throw urlError;
        }
      }
      if (!isTrustedS3Url(url)) {
        const fatal = createAttachmentError(new Error('Untrusted upload URL'), 'Failed to upload file');
        fatal.fatal = true;
        throw fatal;
      }
      const { etag } = await uploadPart({ url, blob, onProgress: onPartProgress, signal });
      if (!etag) {
        // No ETag on a 2xx PUT means the bucket CORS is missing ExposeHeaders: [ETag] —
        // deterministic, so retrying only re-uploads the same bytes for nothing.
        const fatal = createAttachmentError(
          new Error('Missing ETag on uploaded part (bucket CORS must expose the ETag header)'),
          'Failed to upload file'
        );
        fatal.fatal = true;
        throw fatal;
      }
      return etag;
    } catch (e) {
      if (e && e.fatal) throw e;
      if (signal && signal.aborted) throw e;
      lastError = e;
      // 403 → the presigned URL expired; force a fresh one on the next attempt.
      if (getStatusCode(e) === 403) {
        url = null;
      }
      if (attempt < PART_MAX_RETRIES - 1) {
        await delay(RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
      }
    }
  }

  throw createAttachmentError(lastError || new Error('Part upload failed'), 'Failed to upload file');
};

const isAbortError = (error, signal) =>
  Boolean(signal && signal.aborted) || error?.message === 'Upload aborted' || error?.message === 'Upload cancelled';

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

/**
 * Resume-aware multipart upload of a single file. Internally initiates (or resumes),
 * uploads all parts with bounded concurrency, then completes the upload.
 *
 * @param {Object} args
 * @param {string} args.prebookingId
 * @param {File} args.file
 * @param {function} [args.onProgress] - called as onProgress(loadedTotal, file.size)
 * @param {AbortSignal} [args.signal] - cancels the upload (aborts the S3 multipart)
 * @param {number|null} [args.unitScope] - translation scope estimate (passed to initiate)
 * @param {string|null} [args.unitType] - translation scope unit (passed to initiate)
 * @returns {Promise<{success: boolean, attachment: object}>}
 */
export const uploadFileMultipart = async ({ prebookingId, file, onProgress, signal, unitScope = null, unitType = null }) => {
  const fileName = file.name;
  const fileSize = file.size;
  const contentType = file.type || 'application/octet-stream';
  // Resume key includes file identity so multiple large files in one prebooking
  // don't clobber each other's resume record.
  const rKey = resumeKey(prebookingId, fileName, fileSize);

  purgeExpiredResumeEntries();

  let attachmentId = null;
  let partSize = PART_SIZE;
  const uploadedPartsMap = new Map(); // partNumber -> { etag, size }

  // 1. Resume detection: a matching in-flight upload for this prebooking + file.
  //    Resume only when the server-side upload matches this exact file: same declared
  //    size and same part size — otherwise part byte ranges would not line up.
  const existing = readResumeEntry(rKey);
  if (existing && existing.attachmentId) {
    try {
      const status = await getMultipartStatus(prebookingId, existing.attachmentId);
      const resumable = status &&
        status.status === 'uploading' &&
        (status.fileSize === undefined || status.fileSize === fileSize) &&
        (status.partSize === undefined || status.partSize === (existing.partSize || PART_SIZE));
      if (resumable) {
        attachmentId = existing.attachmentId;
        partSize = status.partSize || existing.partSize || PART_SIZE;
        (status.uploadedParts || []).forEach((p) => {
          uploadedPartsMap.set(p.partNumber, { etag: p.etag, size: p.size });
        });
      } else {
        // expired / gone / already finished / mismatching upload — start fresh.
        clearResumeEntry(rKey);
      }
    } catch (e) {
      clearResumeEntry(rKey);
    }
  }

  // 2. Initiate a new multipart upload if we did not resume one.
  if (!attachmentId) {
    const initiated = await initiateMultipart(prebookingId, { filename: fileName, fileSize, contentType, unitScope, unitType });
    attachmentId = initiated.attachmentId;
    partSize = initiated.partSize || PART_SIZE;
    writeResumeEntry(rKey, {
      prebookingId,
      attachmentId,
      uploadId: initiated.uploadId,
      fileName,
      fileSize,
      partSize,
      createdAt: Date.now(),
    });
  }

  const allParts = computeParts(fileSize, partSize);

  // Progress aggregation across parts. Index by partNumber (1-based).
  const partLoaded = new Array(allParts.length + 1).fill(0);
  const completedEtags = new Map(); // partNumber -> etag

  // Pre-account already-uploaded parts so resumed progress starts correctly.
  // A stored part is only skipped when its size matches the expected byte range —
  // a truncated/mismatched part is re-uploaded instead of trusted.
  allParts.forEach((part) => {
    const done = uploadedPartsMap.get(part.partNumber);
    if (done && done.etag && done.size === part.end - part.start) {
      partLoaded[part.partNumber] = part.end - part.start;
      completedEtags.set(part.partNumber, done.etag);
    }
  });

  const emitProgress = () => {
    if (typeof onProgress !== 'function') return;
    let sum = 0;
    for (let i = 1; i < partLoaded.length; i += 1) sum += partLoaded[i];
    onProgress(Math.min(sum, fileSize), fileSize);
  };
  emitProgress();

  const pendingParts = allParts.filter((part) => !completedEtags.has(part.partNumber));

  // Internal controller so that when one part fails for good, the sibling in-flight
  // XHRs of the same batch are aborted instead of uploading into a dead attempt.
  // Linked to the caller's signal so external cancel still aborts everything.
  const internalAbort = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const partSignal = internalAbort ? internalAbort.signal : signal;
  if (internalAbort && signal) {
    if (signal.aborted) {
      internalAbort.abort();
    } else {
      signal.addEventListener('abort', () => internalAbort.abort());
    }
  }

  try {
    // Upload pending parts in batches aligned with the concurrency limit, requesting
    // that batch's presigned URLs on demand so they stay fresh.
    for (let i = 0; i < pendingParts.length; i += UPLOAD_CONCURRENCY) {
      if (signal && signal.aborted) {
        throw createAttachmentError(new Error('Upload aborted'), 'Upload cancelled');
      }
      const batch = pendingParts.slice(i, i + UPLOAD_CONCURRENCY);
      const urlRes = await getPartUrls(prebookingId, attachmentId, batch.map((part) => part.partNumber));
      const prefetched = (urlRes.urls || []).map((u) => [u.partNumber, u.url]);
      const getUrlForPart = makeUrlProvider(prebookingId, attachmentId, prefetched);

      await Promise.all(batch.map(async (part) => {
        const blob = file.slice(part.start, part.end);
        const etag = await uploadPartWithRetry({
          part,
          blob,
          getUrlForPart,
          signal: partSignal,
          onPartProgress: (loaded) => {
            partLoaded[part.partNumber] = loaded;
            emitProgress();
          },
        });
        partLoaded[part.partNumber] = part.end - part.start;
        completedEtags.set(part.partNumber, etag);
        emitProgress();
      }));
    }

    // 3. Complete with ETags sorted ascending by partNumber (S3 requirement).
    const parts = Array.from(completedEtags.entries())
      .map(([partNumber, etag]) => ({ partNumber, etag }))
      .sort((a, b) => a.partNumber - b.partNumber);

    const result = await completeMultipart(prebookingId, attachmentId, parts);
    clearResumeEntry(rKey);
    return result;
  } catch (e) {
    // Stop the sibling in-flight part uploads of the failed batch.
    if (internalAbort && !internalAbort.signal.aborted) {
      internalAbort.abort();
    }

    if (isAbortError(e, signal)) {
      // User cancelled — clean up the S3 multipart server-side and drop the resume entry.
      try {
        await abortMultipart(prebookingId, attachmentId);
      } catch (abortErr) {
        console.error('Failed to abort multipart upload after cancel:', abortErr);
      }
      clearResumeEntry(rKey);
      throw e instanceof AttachmentError ? e : createAttachmentError(e, 'Upload cancelled');
    }

    // Fatal / non-resumable failures clear the resume entry. Transient errors keep it so a
    // later attempt (this session or the next) can resume the already-uploaded parts.
    const status = getStatusCode(e);
    if (e?.fatal || status === 409 || status === 410) {
      if (e?.fatal) {
        // The upload can never complete (untrusted URL / missing ETag / bad state) —
        // release the server-side multipart upload instead of leaving it to the
        // 7-day lifecycle reaper. 409/410 mean it is already finalised or gone.
        try {
          await abortMultipart(prebookingId, attachmentId);
        } catch (abortErr) {
          console.error('Failed to abort multipart upload after fatal error:', abortErr);
        }
      }
      clearResumeEntry(rKey);
    }
    throw e instanceof AttachmentError ? e : createAttachmentError(e, 'Failed to upload file');
  }
};
